import { useEffect, useMemo, useRef, useState } from 'react';
import { useProducts } from '../useProducts';
import { useCart } from './CartContext';
import { cartLineKey, normalizeStockQty } from './cartUtils';

/**
 * Align cart quantities with live catalog stock (MySQL). Runs when products load.
 */
export function useCartStockSync({ enabled = true } = {}) {
  const { products, loading } = useProducts();
  const { lines, reconcileStock } = useCart();
  const [notice, setNotice] = useState('');
  const lastSyncKey = useRef('');

  const stockByKey = useMemo(() => {
    const map = new Map();
    if (!products || typeof products !== 'object') return map;
    for (const [slug, list] of Object.entries(products)) {
      if (!Array.isArray(list)) continue;
      for (const p of list) {
        const id = String(p?.id || '').trim();
        if (!id) continue;
        map.set(cartLineKey(slug, id), normalizeStockQty(p.stock_qty));
      }
    }
    return map;
  }, [products]);

  const stockFingerprint = useMemo(() => {
    const parts = [];
    for (const line of lines) {
      const key = cartLineKey(line.categorySlug, line.productId);
      if (!stockByKey.has(key)) continue;
      const custom = String(line.customization || '').trim();
      parts.push(`${cartLineKey(line.categorySlug, line.productId, custom)}:${stockByKey.get(key)}`);
    }
    return parts.sort().join('|');
  }, [lines, stockByKey]);

  useEffect(() => {
    if (!enabled || loading || lines.length === 0 || stockByKey.size === 0) {
      if (!enabled || (!loading && lines.length === 0)) setNotice('');
      return;
    }

    const syncKey = `${lines.map((l) => `${l.categorySlug}::${l.productId}:${l.qty}`).join('|')}|${stockFingerprint}`;
    if (syncKey === lastSyncKey.current) return;

    const { removed, adjusted } = reconcileStock(stockByKey);
    lastSyncKey.current = syncKey;

    const parts = [];
    if (removed.length) {
      parts.push(
        removed.length === 1
          ? `${removed[0]} is no longer available and was removed.`
          : `${removed.length} items are no longer available and were removed.`
      );
    }
    if (adjusted.length) {
      parts.push(
        adjusted.length === 1
          ? `Quantity for ${adjusted[0]} was reduced to match stock.`
          : `Some quantities were reduced to match current stock.`
      );
    }
    setNotice(parts.join(' '));
  }, [enabled, loading, lines, stockFingerprint, stockByKey, reconcileStock]);

  return { stockNotice: notice, stockLoading: loading };
}
